import { useCallback, useMemo, useState } from 'react';
import api, { ApiError } from '../api/client.js';
import { todayStr } from '../lib/dates.js';
import { canEdit as canEditRole } from '../lib/constants.js';
import { useMeetings } from '../state/useMeetings.js';
import Stats from '../components/Stats.jsx';
import DateNav from '../components/DateNav.jsx';
import MeetingCard from '../components/MeetingCard.jsx';
import MeetingModal from '../components/MeetingModal.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import PrintDocument from '../print/PrintDocument.jsx';
import { printSchedule } from '../print/printSchedule.js';

export default function Day({ user, settings, hasLetterhead, showToast }) {
  const [selectedDate, setSelectedDate] = useState(() => todayStr());
  const [modalState, setModalState] = useState(null); // {meeting} | {meeting:null}
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [printing, setPrinting] = useState(false);

  const { meetings, loading, error, refresh } = useMeetings(selectedDate);
  const editable = canEditRole(user);

  // The server returns each day already ordered by time; sorting here keeps the
  // view correct even if that ever changes.
  const ordered = useMemo(
    () => [...meetings].sort((a, b) => a.time.localeCompare(b.time)),
    [meetings]
  );

  const handleStatus = useCallback(
    async (meeting, status) => {
      try {
        await api.setMeetingStatus(meeting.id, status);
        await refresh();
        showToast(
          status === 'مكتمل'
            ? 'تم تعليم الموعد كمنجز'
            : status === 'ملغى'
              ? 'تم إلغاء الموعد'
              : 'تمت إعادة الموعد إلى قائمة القادم'
        );
      } catch (caught) {
        showToast(
          caught instanceof ApiError ? caught.message : 'تعذّر تحديث حالة الموعد',
          'error'
        );
      }
    },
    [refresh, showToast]
  );

  const handleDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.deleteMeeting(pendingDelete.id);
      await refresh();
      setPendingDelete(null);
      showToast('تم حذف الموعد');
    } catch (caught) {
      showToast(
        caught instanceof ApiError ? caught.message : 'تعذّر حذف الموعد',
        'error'
      );
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, refresh, showToast]);

  const handlePrint = useCallback(async () => {
    setPrinting(true);
    try {
      // Waits for the letterhead image to decode before opening the print
      // dialog, so the artwork is never missing from the first page.
      await printSchedule();
    } finally {
      setPrinting(false);
    }
  }, []);

  return (
    <>
      <Stats meetings={ordered} selectedDate={selectedDate} />

      <DateNav
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        onPrint={handlePrint}
      />

      {loading && ordered.length === 0 ? (
        <div className="loading-state">...جاري تحميل المواعيد</div>
      ) : error ? (
        <div className="error-state" role="alert">
          <p style={{ margin: '0 0 10px' }}>{error}</p>
          <button type="button" className="btn btn-sm" onClick={refresh}>
            إعادة المحاولة
          </button>
        </div>
      ) : ordered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-mark" aria-hidden="true">
            ✦
          </div>
          <h3>لا توجد مواعيد في هذا اليوم</h3>
          {/* The governor's display should not invite data entry. */}
          {editable && <p>يمكنك إضافة أول موعد من زر «موعد جديد».</p>}
        </div>
      ) : (
        <div className="timeline">
          {ordered.map((meeting) => (
            <MeetingCard
              key={meeting.id}
              meeting={meeting}
              canEdit={editable}
              onEdit={(target) => setModalState({ meeting: target })}
              onStatus={handleStatus}
              onDelete={setPendingDelete}
            />
          ))}
        </div>
      )}

      {editable && (
        <button
          type="button"
          className="fab"
          onClick={() => setModalState({ meeting: null })}
        >
          <span aria-hidden="true">＋</span> موعد جديد
        </button>
      )}

      {modalState && (
        <MeetingModal
          meeting={modalState.meeting}
          defaultDate={selectedDate}
          onClose={() => setModalState(null)}
          onSaved={async (message) => {
            setModalState(null);
            await refresh();
            showToast(message);
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="حذف الموعد"
          message={`هل تريد حذف الموعد «${pendingDelete.title}»؟`}
          detail="الحذف نهائي ولا يمكن التراجع عنه. إذا كان الموعد قائماً ولم ينعقد، فالأفضل إلغاؤه بدلاً من حذفه ليبقى في السجل."
          confirmLabel="حذف نهائي"
          busy={deleting}
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      {/* Hidden on screen; the browser's print stylesheet reveals it. */}
      <PrintDocument
        meetings={ordered}
        selectedDate={selectedDate}
        settings={settings}
        hasLetterhead={hasLetterhead}
      />

      {printing && <div className="visually-hidden">جاري تجهيز الطباعة</div>}
    </>
  );
}
